import React, { useState, useEffect, useRef } from 'react';
import T from 'prop-types';
import classnames from 'classnames';
import Spinner from '../../platformui/Spinner';
import { usePagination } from '../../platformui/Hooks';
import { loadScript } from '../../platformui/Base/Async';

import { isFalseOrEmpty, throttle } from '../../platformui/Helpers/helpers';
import ToggleContainer from '../../platformui/Containers/toggleContainer';
import ExpandableButton from '../../platformui/Buttons';
import { onCookieChange } from '../../platformui/Context/cookies';
import Alert from '../../platformui/Alert';
import * as Render from '../../platformui/Base/Render';
import GraphBlock from '../../blocks/graph-block';
import HighlightBlock from '../../blocks/highlight-block';
import LegacyReport from './powerbiLegacy';

import * as util from './helpers';
import SearchField from './searchfield';
import Chevron from '../icons/chevron';

// If a filter has more then this amount if items
// filters should only be available by search.
const MAX_ITEMS_BEFORE_SEARCH = 100;

const FilterCheckbox = (props) => {
  // Click on the label should not trigger a change on
  // the checkbox but open next panel.
  // So we the empty <label> is for styling the custom checbbox.
  // And the span is the fitler label text.
  // Opening the panel is handeled onclick on the parent <li> element.
  //
  // -- and yes. this is not very accessible. but accepeted in this case.
  //
  const {
    selected,
    item,
    column,
    table,
    id,
    label,
    name,
    Filternamn,
    checked,
  } = props;

  // We can pass checked as a prop. Then we use this.
  // Used for the Markera Alla button.
  const isChecked =
    typeof checked !== 'undefined'
      ? checked
      : selected.some(
          util.hasFilterItem({ column, table, name: Filternamn, value: item })
        );

  return (
    <>
      <input
        data-checked={isChecked.toString()}
        className="filters__hidecheckbox"
        checked={isChecked}
        onChange={() => props.onChange(item)}
        id={`${id}__${table}[${column}][${item}]`}
        type="checkbox"
        name={name || `${id}-${table}[${column}][]`}
        value={item}
      />
      <label
        className="filters__checkbox"
        htmlFor={`${id}__${table}[${column}][${item}]`}
      />
      <span className="filters__label">
        {util.formatFilterName(label || item)}
      </span>
    </>
  );
};

const FilterNavigation = (props) => {
  const [childrenToRender, setChildrenToRender] = useState({});
  const { Filternamn, id, table, column, selected, filters, isSearch } = props;
  // No need to render if filters are empty
  // ie. user should do a search.
  if (filters.length === 0) {
    return null;
  }

  const isAllSelected = util.isAllSelected({
    filters,
    selected,
    name: Filternamn,
    column,
    table,
  });

  // Whe n we search we have a flat structure.
  // And get col and table from the item.
  // We also do not render chidlren.
  let listToRender = filters.map((item) => ({
    item: item['Värde'],
    table: isSearch ? item['Tabell'] : props.table,
    column: isSearch ? item['Kolumn'] : props.column,
    children:
      isSearch || isFalseOrEmpty(item['Värden'])
        ? null
        : {
            table: item['Tabell'],
            column: item['Kolumn'],
            filters: item['Värden'],
          },
  }));

  // This is a recursion!
  // This is run and rednered after the first <ul>
  const renderChildren = () => {
    if (!childrenToRender.filters) return null;
    if (isSearch) return null;
    // Make sure that we do not render children from another filter as react
    // will reuse the component and keep the childrenToRender state
    // when the item changes.
    if (!listToRender.some(util.objKeyIs('item', childrenToRender.parent))) {
      return null;
    }
    return <FilterNavigation {...props} {...childrenToRender} />;
  };

  // TODO: Fix the aria-labeledby for the <ul>. What label?
  return (
    <React.Fragment>
      <ul
        data-table={table}
        data-column={column}
        className={classnames({
          filters__list: true,
          'filters__list--unstyled': true,
          'filters__group--vertical': true,
          'filters__list--searchresult': isSearch,
        })}
        aria-labelledby={`filter_for_${table}_${column}`}
        role="group"
      >
        {!isSearch && (
          <li
            className={classnames('filters__listitem')}
            data-checked={isAllSelected}
          >
            <FilterCheckbox
              id={id}
              selected={selected}
              checked={isAllSelected}
              table={table}
              Filternamn={Filternamn}
              column={column}
              item={util.CONST_ALL}
              label={props.Translations.selectall}
              onChange={() => {
                props.selectAll(
                  Filternamn,
                  column,
                  table,
                  listToRender,
                  isAllSelected
                );
              }}
            />
          </li>
        )}

        {listToRender.map(({ item, children, table, column }) => {
          return (
            <li
              data-item={item}
              data-children={childrenToRender.item}
              className={classnames('filters__listitem', {
                'filters__listitem--has-children': children,
                'filters__listitem--selected':
                  children && childrenToRender.parent === item,
              })}
              onClick={() => {
                if (children) {
                  setChildrenToRender({
                    parent: item,
                    ...children,
                  });
                }
              }}
              key={[Filternamn, column, table, item].join('_')}
              data-checked={selected.some(
                util.hasFilterItem({
                  column,
                  table,
                  name: Filternamn,
                  value: item,
                })
              )}
            >
              <FilterCheckbox
                id={id}
                Filternamn={Filternamn}
                selected={selected}
                table={table}
                column={column}
                item={item}
                onChange={() => props.onChange(column, table, item)}
              />
            </li>
          );
        })}
      </ul>
      {renderChildren()}
    </React.Fragment>
  );
};

const Filters = (props) => {
  const {
    items,
    Filternamn,
    column,
    table,
    filter,
    id,
    changeHandler,
    batchAddFilters,
    query,
    setQuery,
  } = props;

  const ITEMS_PER_PAGE = 100;

  const [searchResult, setSearchResult] = React.useState({
    query,
    result: [],
  });

  const originalItems = filter.Filter;
  const isSearch = Boolean(query.length > 2);

  const throttledSearchResult = React.useRef(
    throttle((query) => {
      let result = util
        .flattenFilters(originalItems, table, column)
        .filter(util.searchFilterItem(query));
      let uniqueResult = util.removeDuplicates(result);
      setSearchResult({ query, result: uniqueResult });
    }, 200)
  );

  const [filterSelectionOpen, setFilterSelectionOpen] = useState(false);
  const closeFilterButton = useRef(null);
  // If we have more then 100 items we force a search and do not display a list.
  const initialItems =
    originalItems['Värden'].length < MAX_ITEMS_BEFORE_SEARCH
      ? originalItems['Värden']
      : [];
  // We pass this to FilterNavigation
  // To know how to render sub-panels.

  const selectedValues = util.getSelectedValues(items);

  const _filteredItems = isSearch ? searchResult.result : initialItems;
  useEffect(
    function useThrottleEffectSearch() {
      if (isSearch) {
        throttledSearchResult.current(query);
      }
    },
    [query, isSearch]
  );

  const { next, jump, currentData, currentPage, maxPage } = usePagination(
    _filteredItems,
    ITEMS_PER_PAGE,
    true
  );
  // The query length changes reset pagination to first page.
  useEffect(() => {
    jump(1);
  }, [query.length]);

  const filteredItems = isSearch ? _filteredItems : currentData();

  const onChange = (column, table, value) => {
    // Use `Filternamn` from the outer scope.
    changeHandler(Filternamn, column, table, value);
  };

  const selectAll = (Filternamn, column, table, filterItems, isAllSelected) => {
    if (isAllSelected) {
      changeHandler(Filternamn, column, table, util.CONST_ALL);
    } else {
      batchAddFilters(Filternamn, [
        { item: util.CONST_ALL, column, table },
        ...filterItems,
      ]);
    }
  };

  const unSelectAll = () => {
    changeHandler(Filternamn, null, null, util.CONST_ALL);
  };

  const hasSelectedValues = selectedValues.length > 0;

  /* Render filter section */
  return (
    <>
      <input
        type="hidden"
        value={JSON.stringify(selectedValues)}
        id={`__VALUES__${Filternamn}`}
      />
      <div
        className="filters__group filters__group--add"
        hidden={filterSelectionOpen ? undefined : true}
      >
        <div className="filters__group-headline">
          {props.Translations.addfilters}
          <button
            className="filters__icon-box-close"
            ref={closeFilterButton}
            type="button"
            onClick={() => setFilterSelectionOpen(false)}
          >
            {props.Translations.closefilterselection}
          </button>
        </div>

        <SearchField
          className="unstyled-formfield"
          placeholder={`${props.Translations.searchin} ${Filternamn}`}
          value={query}
          changeHandler={setQuery}
        />
        {isSearch && (
          <div id={['searchhits', table, column].join('_')}>
            {_filteredItems.length} {props.Translations.searchhits}{' '}
            <strong>{searchResult.query}</strong>
          </div>
        )}
        <div
          className={classnames('filters__lists', {
            'filters__lists--search': isSearch,
          })}
        >
          <FilterNavigation
            id={id}
            Translations={props.Translations}
            isSearch={isSearch}
            onChange={onChange}
            selectAll={selectAll}
            Filternamn={Filternamn}
            column={column}
            table={table}
            selected={items}
            filters={filteredItems}
          />
        </div>
        {isSearch && currentPage !== maxPage && (
          <button type="button" onClick={next}>
            {props.Translations.showmore}
          </button>
        )}
      </div>

      <div className="filters__group">
        <div className="filters__group-headline">
          {props.Translations.selectedvalues}

          {hasSelectedValues && (
            <button
              className="filters__clear-all"
              onClick={() => unSelectAll()}
              type="button"
            >
              {props.Translations.clearall}
            </button>
          )}
          {!filterSelectionOpen && (
            <button
              type="button"
              className="filters__icon-box-plus"
              onClick={() => {
                setFilterSelectionOpen(true);
                requestAnimationFrame(() => {
                  closeFilterButton.current.focus();
                });
              }}
            >
              {props.Translations.addfiltersbutton}
            </button>
          )}
        </div>

        {/* 
          Here we render selected filters for this group. 
          Cickon a button should 
          remove the filter and rerender the graph.
        */}
        {!hasSelectedValues && <p>{props.Translations.nofiltersselected}</p>}
        {hasSelectedValues && (
          <div className="filters__selected-values">
            {selectedValues.map((value) => (
              <React.Fragment
                key={[Filternamn, column, table, value].join('_')}
              >
                <button
                  className="filters__remove-value filters__clear"
                  type="button"
                  onClick={() => onChange(null, null, value)}
                >
                  {util.removeZeroTime(value)}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const Statistic = (props) => {
  const { ReportID, SectionID, NoConsentText } = props;
  const Blocks = {
    GraphBlock,
    HighlightBlock,
  };
  // Power BI States
  const [powerBIApiLoaded, setPowerBIApiLoaded] = useState('PENDING');
  const [token, setToken] = useState();
  const [link, setLink] = useState();

  // Filter states
  const [isFilterDataLoading, setIsFilterDataLoading] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [filterQueries, setFilterQueries] = useState({});

  const isCookieAllowed = onCookieChange('PowerBi', ({ isAllowed }) => {
    // This will only run when cookie change.
    if (isAllowed) window.location.reload();
  });

  useEffect(() => {
    if (!isCookieAllowed) {
      return;
    }

    // prevent multiple loading of powerbi script.
    if (!window['powerbi-client']) {
      loadScript(
        'pwt-powerbi-script',
        '/build/inera/static/js/powerbi.min.js',
        (error) => {
          if (!error) {
            setPowerBIApiLoaded('DONE');
          } else {
            console.error('powerbi not loaded', error);
            setPowerBIApiLoaded('ERROR');
          }
        }
      );
    }

    // Filter is loading
    setIsFilterDataLoading(true);

    async function fetchFilterDataAsync() {
      await fetch(
        `/api/GetPowerBIFilterData?reportId=${ReportID}&sectionId=${SectionID}`
      )
        .then((res) => res.json())
        .then((data) => {
          setFilterData(JSON.parse(data));
          // Filter is done loading
          setIsFilterDataLoading(false);
        })
        .catch(() => {
          // Error!
          // Filter is not loading
          setIsFilterDataLoading(false);
        });
    }

    // Do the initial fetch of filterdata
    fetchFilterDataAsync();
  }, []);

  async function requestToken() {
    await fetch(`/api/powerbiembedtoken?reportId=${ReportID}`)
      .then((res) => res.json())
      .then((json) => {
        if (link !== json.Link) {
          setLink(json.Link);
        }
        if (token !== json.Token.token) {
          setToken(json.Token.token);
        }
      })
      .catch(() => setPowerBIApiLoaded('TOKEN_ERROR'));
  }

  useEffect(async () => {
    if (!isCookieAllowed) {
      return;
    }
    await requestToken();
    const interval = setInterval(async () => {
      await requestToken();
    }, 1000 * 60 * 10); // 10 minutes, BE returns tokens valid at least 15 minutes.
    return () => clearInterval(interval);
  }, []);

  async function changeHandler(name, column, table, value) {
    //  create a mutable copy of the set
    let filters = [...currentFilters];

    await requestToken();

    if (table && column) {
      let filterItem = util.createFilterItem({ name, column, table, value });
      // Handle click on ALL
      if (value === util.CONST_ALL) {
        // If it IS a click on ALL
        // All the other in same column and table should be disabled.
        //  (we do the adding of filters in batchAddFilters
        // called from <Filter> component)
        filters = util.deleteFromArray(
          util.inTableColumn(table, column),
          filters
        );
      } else {
        filters = util.toggleFilterValue(filterItem, filters);
      }
    } else if (value === util.CONST_ALL) {
      filters = util.deleteFromArray(util.objKeyIs('name', name), filters);
    } else {
      filters = util.deleteFromArray((obj) => {
        return obj.name === name && obj.value === value;
      }, filters);
    }
    setCurrentFilters(filters);
  }

  const batchAddFilters = (Filternamn, items) => {
    const copy = [...currentFilters];
    items.forEach(({ column, table, item }) => {
      const isSelected = copy.some(
        util.hasFilterItem({ column, table, name: Filternamn, value: item })
      );
      if (!isSelected) {
        copy.push(
          util.createFilterItem({
            name: Filternamn,
            column,
            table,
            value: item,
          })
        );
      }
    });
    setCurrentFilters(copy);
  };

  const filterQueryHandler = (filternamn, queryState) => (query) => {
    setFilterQueries({
      ...queryState,
      [filternamn]: query,
    });
  };

  const filterGroups = currentFilters.reduce((acc, cur) => {
    const copy_of_acc = [...acc];
    const { table, column, value } = cur;
    const index = copy_of_acc.findIndex(util.inTableColumn(table, column));
    if (index === -1) {
      copy_of_acc.push({
        table: table,
        column: column,
        items: [value],
      });
    } else {
      const group = copy_of_acc[index];
      group.items = [...group.items, value];
      copy_of_acc[index] = group;
    }
    return copy_of_acc;
  }, []);

  if (isCookieAllowed) {
    return (
      <div className="statistics-block">
        {!props.DisableFilters && (
          <div className="filters" aria-busy={isFilterDataLoading}>
            <ToggleContainer open={false}>
              {({ open, toggle }) => (
                <>
                  <ExpandableButton
                    attr={{ 'aria-label': props.Translations.filterheadline }}
                    buttonClass="filters__headline iu-tt-uppercase filters__toggle-button"
                    controlsElementId={[
                      'FilterWrapper',
                      ReportID,
                      SectionID,
                    ].join('_')}
                    onClick={toggle}
                    open={open}
                    buttonText={
                      <>
                        {props.Translations.filterheadline}{' '}
                        <span
                          className={classnames({
                            filters__chevron: true,
                            'filters__chevron--is-open': open,
                            'iu-opacity-0': isFilterDataLoading,
                          })}
                        >
                          <Chevron />
                        </span>
                      </>
                    }
                  />

                  <div
                    className="filters__wrapper"
                    id={['FilterWrapper', ReportID, SectionID].join('_')}
                    hidden={!open}
                  >
                    {isFilterDataLoading && <Spinner alt={true} />}
                    {filterData.map(function (filter) {
                      const items = util.getFiltersByName(
                        currentFilters,
                        filter.Filternamn
                      );

                      const filterCount = util.getSelectedValues(items).length;
                      let filterCountText = filter.Filternamn;

                      // Add number to headline/button when we have filters
                      if (filterCount > 0) {
                        filterCountText = (
                          <>
                            {filter.Filternamn}
                            {` `}
                            <span className="iu-color-grey-700 iu-tt-none iu-fw-normal">
                              ({filterCount}
                              {` `}
                              {filterCount === 1
                                ? props.Translations.oneselected
                                : props.Translations.manyselected}
                              )
                            </span>
                          </>
                        );
                      }

                      return (
                        <ToggleContainer
                          key={[
                            'filter',
                            'group',
                            filter.Filter.Tabell,
                            filter.Filter.Kolumn,
                          ].join('_')}
                          open={false}
                        >
                          {({
                            open: filterGroupOpen,
                            toggle: toggleFilterGroup,
                          }) => (
                            <>
                              <ExpandableButton
                                buttonClass="filters__toggle-button filters__group-button"
                                open={filterGroupOpen}
                                controlsElementId={`filter-${filter.Filternamn}`}
                                buttonText={filterCountText}
                                onClick={toggleFilterGroup}
                              />
                              <div
                                className="filters__navigation"
                                id={`filter-${filter.Filternamn}`}
                                hidden={filterGroupOpen ? undefined : true}
                              >
                                <Filters
                                  id={`${props.ReportID}-${props.SectionID}`}
                                  batchAddFilters={batchAddFilters}
                                  Translations={props.Translations}
                                  key={[
                                    filter.Filternamn,
                                    filter.Filter.RapportID,
                                  ].join('_')}
                                  items={items}
                                  changeHandler={changeHandler}
                                  query={
                                    filterQueries[`${filter.Filternamn}`] || ''
                                  }
                                  setQuery={filterQueryHandler(
                                    filter.Filternamn,
                                    filterQueries
                                  )}
                                  Filternamn={filter.Filternamn}
                                  column={filter.Filter.Kolumn}
                                  table={filter.Filter.Tabell}
                                  filter={filter}
                                />
                              </div>
                            </>
                          )}
                        </ToggleContainer>
                      );
                    })}
                  </div>
                </>
              )}
            </ToggleContainer>
          </div>
        )}
        {props.Heading && <h2>{props.Heading}</h2>}
        {props.Preamble && <p>{props.Preamble}</p>}
        {props.HighlightContentTop && (
          <div style={{ marginBottom: 20 }}>
            <Render.Block
              blocks={props.HighlightContentTop}
              propertyName="HighlightContentTop"
              currentPageProps={props}
              AnchorSectionId={props.HighlightAnchorSectionIdTop}
              Heading={
                props.HighlightContentTop[0]?.Heading ||
                props.HighlightContentTop.Heading // remove this line when removing old highlight block
              }
            >
              <Render.BlockTypeRenderer Blocks={Blocks} />
            </Render.Block>
          </div>
        )}

        {link && token && !props.SectionID && (
          <Render.Block
            blocks={props.MainContent}
            propertyName="MainContent"
            currentPageProps={props}
            apiLoaded={powerBIApiLoaded}
            filters={filterGroups}
            url={link}
            token={token}
          >
            <Render.BlockTypeRenderer Blocks={Blocks} />
          </Render.Block>
        )}

        {link && token && props.SectionID && (
          <LegacyReport
            translations={props.Translations}
            enableExport={!props.DisableExport}
            apiLoaded={powerBIApiLoaded}
            filters={filterGroups}
            reportId={props.ReportID}
            url={link}
            token={token}
            sectionId={props.SectionID}
          />
        )}
        {props.HighlightContentBottom && (
          <Render.Block
            blocks={props.HighlightContentBottom}
            propertyName="HighlightContentBottom"
            currentPageProps={props}
            AnchorSectionId={props.HighlightAnchorSectionIdBottom}
            Heading={
              props.HighlightContentBottom[0]?.Heading ||
              props.HighlightContentBottom.Heading // remove this line when removing old highlight block
            }
          >
            <Render.BlockTypeRenderer Blocks={Blocks} />
          </Render.Block>
        )}
      </div>
    );
  } else {
    return (
      <Alert type="warning">
        {<div dangerouslySetInnerHTML={{ __html: NoConsentText }} />}
      </Alert>
    );
  }
};

Statistic.defaultProps = {
  DisableFilters: false,
  DisableExport: false,
};

Statistic.propTypes = {
  Translations: T.shape({
    selectedvalues: T.string,
    filterheadline: T.string,
    loadingfilters: T.string,
    addfilters: T.string,
    addfiltersbutton: T.string,
    closefilterselection: T.string,
    selectall: T.string,
    nofiltersselected: T.string,
    clearall: T.string,
    manyselected: T.string,
    oneselected: T.string,
    searchin: T.string,
    export: T.string,
    searchhits: T.string,
    showmore: T.string,
  }),
  Heading: T.string,
  Preamble: T.string,
  /** Set to true to not show filters */
  DisableFilters: T.bool,
  /** Set to true to not show export */
  DisableExport: T.bool,
  /** Report ID for Power BI Connection */
  ReportID: T.string.isRequired,
  /** Section ID for Power BI Connection */
  SectionID: T.string.isRequired,
  NoConsentText: T.string,
};

export default Statistic;
